import { ActiveAuth, ignoredMessages } from './rollbar';
import { core, safeToNumber } from '@tock/coretypes';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

export type LogArgument = string | Error | object | Date | undefined;

/**
 * Initializes Datadog logging and RUM.
 * Call this once at app startup.
 */
export function initDatadogLogging(config: {
  clientToken: string;
  applicationId: string;
  service: string;
  env: string;
  version: string;
  /** Percentage of logs to send, between 0 and 100 */
  loggingSampleRate?: number;
  /** Percentage of sessions to send for RUM, between 0 and 100 */
  rumSampleRate?: number;
  /** Percentage of sessions to collect for Session Replay, between 0 and 100. */
  sessionReplaySampleRate?: number;
}) {
  if (config.clientToken === '') {
    return;
  }

  datadogLogs.init({
    clientToken: config.clientToken,
    site: 'us5.datadoghq.com',
    service: config.service,
    env: config.env,
    forwardErrorsToLogs: false,
    sessionSampleRate: config.loggingSampleRate ?? 100,
    version: config.version,
    beforeSend: (log) => {
      // Filter out ignored messages
      if (shouldIgnoreError(log.message)) {
        return false;
      }
      return true;
    },
  });

  datadogRum.init({
    applicationId: config.applicationId,
    clientToken: config.clientToken,
    site: 'us5.datadoghq.com',
    service: config.service,
    env: config.env,
    // Filter out RUM events (errors) that match ignored patterns
    beforeSend: (event) => {
      try {
        const ev = event as { message?: unknown; error?: unknown } | undefined;
        let msg = '';
        if (ev && typeof ev.message === 'string') {
          msg = ev.message;
        } else if (ev && typeof ev.error === 'object' && ev.error !== null) {
          const err = ev.error as Record<string, unknown>;
          if (typeof err.message === 'string') {
            msg = err.message as string;
          } else if (typeof err.stack === 'string') {
            msg = err.stack as string;
          }
        }
        if (msg && shouldIgnoreError(msg)) {
          return false;
        }
      } catch (_e) {
        // If anything goes wrong while filtering, don't block the event
      }
      return true;
    },
    sessionSampleRate: config.rumSampleRate ?? 100,
    sessionReplaySampleRate: config.sessionReplaySampleRate ?? 0,
    version: config.version,
    defaultPrivacyLevel: 'mask-user-input',
    trackUserInteractions: true,
    trackLongTasks: true,
    trackResources: true,
  });
}

export function initGlobalLogging() {
  if (typeof window === 'undefined') {
    return;
  }

  function standardizeArgs(...args: LogArgument[]): {
    message?: string;
    error?: Error;
    context?: Record<string, unknown>;
  } {
    let message: string | undefined;
    let error: Error | undefined;
    let context: Record<string, unknown> | undefined;
    if (typeof args[0] === 'string') {
      message = args[0];
      if (args[1] instanceof Error) {
        error = args[1];
      } else if (args[1] && typeof args[1] === 'object') {
        context = args[1] as Record<string, unknown>;
      }
    } else if (args[0] instanceof Error) {
      error = args[0];
      if (args[1] && typeof args[1] === 'object') {
        context = args[1] as Record<string, unknown>;
      }
    } else {
      context = args[0] as Record<string, unknown> | undefined;
    }
    return { message, error, context };
  }

  // Setup global logging functions
  window.LOGGER = {
    error: function (...args: LogArgument[]) {
      // Forward to Rollbar if present
      window.Rollbar?.error?.(...args);
      const { message, error, context } = standardizeArgs(...args);
      // Send to Datadog RUM
      let errorObj = error;
      if (!errorObj) {
        errorObj = message ? new Error(message) : new Error('Unknown error');
      }
      try {
        datadogRum.addError(errorObj, { ...(context ?? {}) });
      } catch (_e) {
        // swallow errors from reporting logic to avoid breaking app
      }
    },
    warn: function (...args: LogArgument[]) {
      window.Rollbar?.warning?.(...args);
      const { message, context } = standardizeArgs(...args);
      datadogLogs.logger.warn?.(message ?? '', context);
    },
    info: function (...args: LogArgument[]) {
      window.Rollbar?.info?.(...args);
      const { message, context } = standardizeArgs(...args);
      datadogLogs.logger.info?.(message ?? '', context);
    },
    debug: function (...args: LogArgument[]) {
      window.Rollbar?.debug?.(...args);
      const { message, context } = standardizeArgs(...args);
      datadogLogs.logger.debug?.(message ?? '', context);
    },
  };
}

/**
 * Checks if an error message should be ignored based on ignoredMessages patterns.
 */
export function shouldIgnoreError(message: string): boolean {
  return ignoredMessages.some((pattern) =>
    typeof pattern === 'string'
      ? typeof message === 'string' && message.includes(pattern)
      : typeof message === 'string' && pattern.test(message)
  );
}

/**
 * Sets user context for Datadog RUM logging.
 * Call this when a patron logs in or their context changes.
 */
export function configureDatadogForPatron(patron: core.Patron, activeAuth: Partial<ActiveAuth>) {
  if (datadogLogs && patron?.id) {
    datadogLogs.setGlobalContext({
      businessId: activeAuth.businessId,
      businessGroupId: activeAuth.businessGroupId,
      id: safeToNumber(patron.id),
      email: patron.email,
    });
    datadogRum.setUser({
      businessId: activeAuth.businessId,
      businessGroupId: activeAuth.businessGroupId,
      id: String(safeToNumber(patron.id)),
      email: patron.email,
    });
  }
}
